<template>
  <div class="tag-list">
    <div class="tag-list-inline">
      <button
        class="tag-list-trigger"
        title="Adicionar tag"
        @click="showTagModal = true"
      >
        <i class="fa-solid fa-tag tag-list-trigger-icon"></i>
        <span>Tag</span>
      </button>

      <div
        v-for="t in currentValueFilter"
        :key="t.id"
        class="tag-list-chip"
        :style="{ background: t.color || 'var(--text-muted)' }"
      >
        <span class="tag-list-chip-name">{{ t.name }}</span>
        <i class="fa-solid fa-xmark tag-list-chip-close" @click.stop="remove(t)"></i>
      </div>
    </div>

    <ModalChild
      v-model="showTagModal"
      title="Tags"
      :width="420"
      body-padding="14px 16px"
      @close="onModalClose"
    >
      <Loading v-if="loading" type="line" />

      <template v-else>
        <div class="tag-list-section-title">Nova tag</div>
        <div class="tag-list-create-row">
          <div class="tag-list-create-input-wrapper">
            <InputText
              title=""
              field="newTagName"
              :formName="formName"
              :maxLength="60"
              :markFormDirty="false"
              :borderRadius="50"
              :enter="createTag"
              v-model="newTagName"
            />
          </div>
          <input v-model="newTagColor" type="color" class="tag-list-create-color" title="Cor da tag" />
          <Button
            _key="btnCreateTag"
            type="primary"
            classIcon="fas fa-plus"
            size="small"
            :disabled="!canCreateTag || creatingTag"
            :clicked="createTag"
          />
        </div>

        <template v-if="currentValueFilter.length">
          <div class="tag-list-section-title">Selecionadas</div>
          <ScrollBar :maxHeight="140" :minHeight="0">
            <ul class="tag-list-items">
              <li
                v-for="t in currentValueFilter"
                :key="'selected-' + t.id"
                class="tag-list-row tag-list-row-on"
                @click="remove(t)"
              >
                <span class="tag-list-swatch" :style="{ background: t.color || 'var(--text-muted)' }"></span>
                <span class="tag-list-name">{{ t.name }}</span>
                <i class="fa-solid fa-check tag-list-check"></i>
              </li>
            </ul>
          </ScrollBar>
        </template>

        <div class="tag-list-section-title">Disponíveis</div>
        <ScrollBar v-if="availableToAdd.length" :maxHeight="220" :minHeight="0">
          <ul class="tag-list-items">
            <li
              v-for="item in availableToAdd"
              :key="item.id"
              class="tag-list-row"
              @click="add(item)"
            >
              <span class="tag-list-swatch" :style="{ background: item.color || 'var(--text-muted)' }"></span>
              <span class="tag-list-name">{{ item.name }}</span>
              <span></span>
            </li>
          </ul>
        </ScrollBar>
        <div v-else class="tag-list-empty">Nenhuma tag disponível</div>
      </template>
    </ModalChild>
  </div>
</template>

<script>
import Loading from "@/ui/shared/Loading.vue";
import ScrollBar from "@/ui/layout/ScrollBar.vue";
import ModalChild from "@/ui/layout/ModalChild.vue";
import InputText from "@/ui/forms/InputText.vue";
import Button from "@/ui/forms/Button.vue";

import TagService from "../services/tag.service";

import { mapMutations } from "vuex";

export default {
  name: "Tags",
  components: { Loading, ScrollBar, ModalChild, InputText, Button },
  props: {
    cardId: {
      type: String,
      required: true,
    },
    initialValue: {
      type: Array,
      default: () => [],
    },
    module: {
      type: String,
      required: true,
    },
    urlCreateUpdate: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      formName: "tagCreateInlineForm",
      availableToAdd: [],
      currentValueFilter: [],
      loading: false,
      showTagModal: false,
      newTagName: "",
      newTagColor: "#577696",
      creatingTag: false,
    };
  },
  created() {
    this.currentValueFilter = this.sortByName(this.initialValue);
  },
  mounted() {
    this.getAll();
  },
  computed: {
    canCreateTag() {
      return this.newTagName.trim().length > 0;
    },
  },
  methods: {
    ...mapMutations("generic", ["removeLoading"]),
    onModalClose() {
      this.newTagName = "";
      this.newTagColor = "#577696";
    },
    sortByName(list) {
      return [...list].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base", numeric: true })
      );
    },
    async getAll() {
      this.loading = true;
      const response = await TagService.getAll(this.module);
      const data = response.data.content.data;
      const selected = new Set(this.currentValueFilter.map((t) => String(t.id).toUpperCase()));
      this.availableToAdd = this.sortByName(
        data.filter((item) => !selected.has(String(item.id).toUpperCase()))
      );
      this.loading = false;
    },
    async createTag() {
      if (!this.canCreateTag || this.creatingTag) return;
      this.creatingTag = true;
      const name = this.newTagName.trim();
      const response = await TagService.create(this.module, { name, color: this.newTagColor });
      const created = this.extractCreated(response);
      if (created) {
        this.add(created);
      } else {
        await this.getAll();
        const match = this.availableToAdd.find(
          (item) => (item.name || "").trim().toUpperCase() === name.toUpperCase()
        );
        if (match) this.add(match);
      }
      this.newTagName = "";
      this.newTagColor = "#577696";
      this.creatingTag = false;
      this.removeLoading(["btnCreateTag"]);
    },
    extractCreated(response) {
      const d = response && response.data;
      if (!d) return null;
      if (d.content && d.content.id) return d.content;
      if (d.content && d.content.data && d.content.data.id) return d.content.data;
      if (d.data && d.data.id) return d.data;
      if (d.id) return d;
      return null;
    },
    async persist() {
      const tags = this.currentValueFilter.map((x) => x.id);
      await TagService.updateTags(this.urlCreateUpdate, this.cardId, tags);
      this.$emit("input", this.currentValueFilter);
    },
    add(item) {
      this.currentValueFilter.push(item);
      this.availableToAdd = this.availableToAdd.filter((x) => String(x.id) !== String(item.id));
      this.currentValueFilter = this.sortByName(this.currentValueFilter);
      this.persist();
    },
    remove(item) {
      this.currentValueFilter = this.currentValueFilter.filter((x) => String(x.id) !== String(item.id));
      if (!this.availableToAdd.some((x) => String(x.id) === String(item.id))) {
        this.availableToAdd.push(item);
        this.availableToAdd = this.sortByName(this.availableToAdd);
      }
      this.persist();
    },
  },
};
</script>

<style scoped>
.tag-list-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.tag-list-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px dashed var(--border-color);
  color: var(--text-secondary);
  border-radius: 999px;
  height: 30px;
  padding: 0 12px 0 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.tag-list-trigger:hover {
  background: var(--bg-hover);
  color: var(--color-brand);
  border-color: var(--color-brand);
}

.tag-list-trigger-icon {
  font-size: 12px;
}

.tag-list-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  border-radius: 999px;
  font-size: 12px;
  padding: 3px 10px;
  margin: 2px 0;
}

.tag-list-chip-close {
  cursor: pointer;
  font-size: 12px;
}


.tag-list-section-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 10px 0 6px;
}

.tag-list-create-row {
  display: grid;
  grid-template-columns: 1fr 42px auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 4px;
}

.tag-list-create-input-wrapper {
  min-width: 0;
}

.tag-list-create-input-wrapper ::v-deep .form-group {
  margin-bottom: 0;
}

.tag-list-create-input-wrapper ::v-deep label {
  display: none;
}

.tag-list-create-color {
  width: 42px;
  height: 34px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  cursor: pointer;
  padding: 2px 3px;
  align-self: center;
}

.tag-list-empty {
  text-align: center;
  padding: 24px;
  font-size: 13px;
  font-style: italic;
  color: var(--text-muted);
}

.tag-list-items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.tag-list-row {
  display: grid;
  grid-template-columns: 14px 1fr 16px;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}

.tag-list-row:hover {
  background: var(--bg-hover);
}

.tag-list-row-on {
  background: var(--bg-elevated);
  border-left: 3px solid var(--color-brand);
}

.tag-list-swatch {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tag-list-name {
  font-size: 13px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag-list-check {
  font-size: 12px;
  color: var(--color-brand);
}
</style>
